import * as React from 'react'
import styled, { useTheme } from 'styled-components'
import Tabs, { TabsProps } from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import { ChevronDown } from 'react-feather'
import _ from 'lodash'
import { StyledTabProps } from './types'
import { Flex } from '../Flex'
import { Text, TextVariant } from '../Text'
import { PopoverMenu, SingleMenuItem } from '../PopoverMenu'

interface StyledTabsProps extends TabsProps {
  fontSize?: number
}

const StyledTabs = styled((props: StyledTabsProps) => <Tabs {...props} />)(({ theme, fontSize }) => ({
  width: '100%',
  '& .MuiTab-root': {
    color: theme.colors.gray3,
    borderBottom: `1px solid ${theme.colors.gray3}`,
    textTransform: 'none',
    padding: 0,
    alignItems: 'start',
    flex: 1,
    maxWidth: 'unset',
    letterSpacing: 0,
    fontSize: fontSize ? `${fontSize}px` : '14px',
    path: {
      stroke: `${theme.colors.gray3} !important`
    }
  },
  '& .MuiTabs-indicator': {
    backgroundColor: theme.colors.purple,
    height: 1
  },
  '& .Mui-disabled': {
    color: `${theme.colors.gray3} !important`
  }
}))

const StyledTab = styled((props: StyledTabProps) => <Tab disableRipple {...props} />)(
  ({ theme, lastItem, activeTab }) => ({
    marginRight: `${!lastItem ? '32px' : '0'} !important`,
    width: 'max-content !important',
    maxWidth: 'unset !important',
    '&:hover': {
      color: `${theme.colors.purple} !important`,
      opacity: 1,
      path: {
        stroke: `${theme.colors.purple} !important`
      }
    },
    '&.Mui-selected': {
      color: `${theme.colors.purple} !important`,

      path: {
        stroke: `${theme.colors.purple} !important`
      }
    },

    '&.Mui-focusVisible': {
      backgroundColor: 'none'
    },
    // Workaround to get active tab color when Popover is implemented
    color: activeTab ? `${theme.colors.purple} !important` : ``
  })
)

interface ComponentProps {
  tabs: StyledTabProps[]
  onChange: (newValue: number) => void
  value: number
  fontSize?: number
  activeTab?: boolean
}

const Component: React.FC<ComponentProps> = ({ tabs, onChange, value, fontSize, activeTab }) => {
  const theme = useTheme()

  return (
    <StyledTabs value={value} onChange={(_, newValue) => onChange(newValue)} variant="scrollable" fontSize={fontSize}>
      {tabs
        .filter((tab) => _.isUndefined(tab.parentValue))
        .map((tab, index) => {
          const children: SingleMenuItem[] = tabs
            .filter((singleTab) => singleTab?.parentValue === tab.value)
            .map((singleTab) => {
              return { label: singleTab.label, onClick: () => onChange(singleTab.value) } as SingleMenuItem
            })
          return _.size(children) ? (
            <PopoverMenu
              onItemClick={(item) => {
                if (!_.isUndefined(item.onClick)) item.onClick()
              }}
              menuItems={children}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
            >
              <StyledTab
                activeTab={activeTab}
                disabled={tab.disabled}
                value={tab.value}
                label={
                  <Flex gap="14px" alignItems="center">
                    <Text
                      clickable
                      variant={tab?.big ? TextVariant.H5 : TextVariant.BODY_DEFAULT_MEDIUM}
                      color="inherit"
                    >
                      {tab.label}
                    </Text>
                    <ChevronDown />
                  </Flex>
                }
                key={tab.value}
                sx={{ minWidth: 'max-content', opacity: 1 }}
              />
            </PopoverMenu>
          ) : (
            <StyledTab
              disabled={tab.disabled}
              value={tab.value}
              label={
                <Flex gap="16px" alignItems="center">
                  {React.isValidElement(tab?.Icon) && React.cloneElement(tab?.Icon)}
                  <Text clickable variant={tab?.big ? TextVariant.H5 : TextVariant.BODY_DEFAULT_MEDIUM} color="inherit">
                    {tab.label}
                  </Text>
                  {React.isValidElement(tab?.tag) && React.cloneElement(tab?.tag)}
                  {!_.isNil(tab?.totalItemsCount) && (
                    <>
                      <Flex
                        alignItems="center"
                        justifyContent="center"
                        ml="16px"
                        pl="16px"
                        borderLeft={`1px solid ${theme.colors.gray1}`}
                        gap="8px"
                      >
                        <Text
                          variant={TextVariant.BODY_SMALL}
                          color={theme.colors.gray9}
                        >{`${tab.totalItemsCount} items`}</Text>
                      </Flex>
                    </>
                  )}
                </Flex>
              }
              key={tab.value}
              lastItem={index === tabs.length - 1}
              sx={{ minWidth: 'max-content' }}
            />
          )
        })}
    </StyledTabs>
  )
}

export default Component
